import "./missionMonitoringDashboard.css";
import "../powerdash.css";

import {
  CheckOption,
  CountValueContainer,
} from "../clientDashboard/components/FilterBar";
import {
  LABEL_ALL,
  MISSION_MONITORING_STATIC_GROUPS,
  MISSION_MONITORING_STATIC_STATUS,
  MiSSION_MONITORING_COMMENT_COUNT,
  MiSSION_MONITORING_FLAG_COUNT,
  MiSSION_MONITORING_STATUS,
  MiSSION_MONITORING_SUBSTATUS,
} from "../constants";
import {
  contractIsActive,
  formatDate,
  getStatusColor,
  humanize,
  missionHasResults,
  missionRequiresClientAction,
  occurrences,
  sumKey,
  uniqueArrayOfObject,
} from "../utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ComponentPlaceholder } from "../components/componentPlaceholder/componentPlaceholder";
import { Container } from "reactstrap";
import { LevelHeader } from "../clientDashboard/components/LevelHeader";
import LoadingImg from "../components/loading";
import { MonitoringView } from "./components/monitoringView/MonitoringView";
import ReactSelect from "react-select";
import download from "downloadjs";
import { fetchMissionsMonitoring } from "../actions/missionMonitoring";
import { fetchUserTrials } from "../actions/user";
import moment from "moment";
import { requestFetchMissionMonitoring } from "../services/backendRequests";
import slugify from "slugify";
import { useLocation } from "react-router-dom";

// `sensor_type` is free text and production writes composites like "RGB + MS".
// Splitting on "+" lets the facet match by containment instead of equality:
// picking RGB returns RGB and RGB + MS, picking MS returns MS and RGB + MS,
// picking RGB + MS returns the flights carrying both and neither one alone.
const sensorParts = (value) =>
  String(value ?? "")
    .split("+")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

// True when any selected value is contained in the mission's own sensor set.
const matchesSensorType = (selectedValues, value) => {
  const owned = new Set(sensorParts(value));
  return selectedValues.some((selected) => {
    const wanted = sensorParts(selected);
    // A blank value ("N/A") has no parts, and an empty subset would match every
    // mission: fall back to strict equality for it.
    if (wanted.length === 0) return selected === value;
    return wanted.every((part) => owned.has(part));
  });
};

// A group selection is either an array (facet checkboxes) or a single scalar
// (a donut/DetailGrid drill combination). Normalise to an array so numeric
// dimensions (Plots, Gsd…) don't blow up on `.includes`, and match by equality,
// the sensor type excepted (see above).
// Iterating the selected criteria, not the mission's own keys: `custom_data` is
// per mission, so a mission missing the dimension never visited the criterion
// and passed the filter untested. The "∅" value stays selectable, it is
// `undefined` in `groups` and matches by equality like any other.
export const shouldBeFilteredByGroups = (elem, groupingObject) => {
  return Object.entries(groupingObject).every(([group, selection]) => {
    if (selection == null) return true;
    const values = Array.isArray(selection) ? selection : [selection];
    if (group === MISSION_MONITORING_STATIC_GROUPS.SENSOR)
      return matchesSensorType(values, elem.groups[group]);
    return values.includes(elem.groups[group]);
  });
};

export const MissionMonitoringDashboard = () => {
  const user = useSelector((state) => state.user);
  // Page is loading waiting for contracts refetch
  const [loading, setLoading] = useState(true);

  // remove inactive contracts (ended or not started)
  const activeContracts = useMemo(
    () => user.contracts.filter((contract) => contractIsActive(contract)),
    [user.contracts]
  );

  // Calculate companies from user contracts
  const companies = uniqueArrayOfObject(
    activeContracts.map((contract) => contract.company),
    "id"
  );
  // Same { label, value } shape as selectedCompany, so onChange stores the
  // picked option as is.
  const companyOptions = companies
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((company) => ({ label: company.name, value: company.id }));

  // Deep-link: /monitoring?contract=<id> (e.g. from a campaign page) pre-selects
  // that campaign's company and narrows the selection to just that contract on
  // first load. Read once from the entry URL.
  const location = useLocation();
  const initialContractId = useMemo(
    () => new URLSearchParams(location.search).get("contract"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialContract = initialContractId
    ? activeContracts.find((contract) => contract.id === initialContractId)
    : undefined;

  const [selectedCompany, setSelectedCompany] = useState(() => {
    const company = initialContract?.company ?? companies[0];
    return { label: company?.name, value: company?.id };
  });
  const [pendingContractId, setPendingContractId] = useState(initialContractId);
  // contracts are refiltered based on the selected company
  const contracts = useMemo(
    () =>
      activeContracts.filter(
        (contract) => contract.company.id === selectedCompany?.value
      ),
    [selectedCompany, activeContracts]
  );

  // Selected contracts are the one ticked by user
  const [selectedContracts, setSelectedContracts] = useState([]);

  // The mission card's globe button is gated on `user.trials`, which redux-persist
  // keeps in localStorage. Nothing on this page refreshed it, so it could be a
  // whole session old: a flight delivered since then had no button until some
  // other page happened to refetch the trials. R3-06.
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchUserTrials());
  }, [dispatch]);

  // Selected contracts are based on contracts and remapped on label value format
  useEffect(() => {
    // First load via a ?contract deep-link → narrow to that single contract
    // (consumed once); afterwards the selection follows the company as before.
    if (pendingContractId) {
      const match = contracts.find(
        (contract) => contract.id === pendingContractId
      );
      if (match) {
        setSelectedContracts([{ label: match.name, value: match.id }]);
        setPendingContractId(undefined);
        clearFilters();
        return;
      }
    }
    setSelectedContracts(
      contracts.map((contract) => {
        return { label: contract.name, value: contract.id };
      })
    );
    clearFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts]);

  // Mission monitorings elements fetched
  const [missionMonitorings, setMissionMonitorings] = useState([]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchMissionsMonitoring(
      selectedContracts.map((contract) => contract.value),
      user
    )
      .then((res) => {
        setMissionMonitorings(res);
        setLoading(false);
      })
      .catch((error) => console.error("Error: ", error));
  }, [selectedContracts, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const statusList = useMemo(
    () => [
      ...new Set([
        ...MISSION_MONITORING_STATIC_STATUS, // Static status sorted
        ...missionMonitorings.map((mm) => mm.status), // Additionnal found status
      ]),
    ],
    [missionMonitorings]
  ); // Defined static status

  // Filters
  const [selectedStatus, setSelectedStatus] = useState(undefined);
  const [filterUnreadComments, setFilterUnreadComments] = useState(false);
  const [filterActiveFlags, setFilterActiveFlags] = useState(false);
  const [filterClientAction, setFilterClientAction] = useState(false);
  const [filterHasResults, setFilterHasResults] = useState(false);
  // Soft filters
  const [filterSiteName, setFilterSiteName] = useState("");
  const [softSelectedStatus, setSoftSelectedStatus] = useState(undefined);
  const [softSelectedSubStatus, setSoftSelectedSubStatus] = useState(undefined);
  const [softGroupingObject, setSoftGroupingObject] = useState(null);
  // Grouping

  // Group keys defined (groups are set in fetchMissionMonitoring: static groups +
  // custom_data keys). Union across every mission, not just the first: the four
  // static keys are always there, but the custom_data ones vary per mission and
  // the column is nullable, so reading only the head of the list silently hid
  // every dimension that mission happened not to carry.
  const groups = useMemo(
    () =>
      missionMonitorings.length > 0
        ? [
            ...new Set(
              missionMonitorings.flatMap((element) =>
                Object.keys(element.groups)
              )
            ),
          ]
        : Object.values(MISSION_MONITORING_STATIC_GROUPS),
    [missionMonitorings]
  );
  const [groupingObject, setGroupingObject] = useState({});

  // Reset grouping object on selectedContracts change
  const [prevSelectedContracts, setPrevSelectedContracts] = useState(null);
  if (selectedContracts !== prevSelectedContracts) {
    setPrevSelectedContracts(selectedContracts);
    setGroupingObject({});
  }

  const groupOptions = useMemo(() => {
    let options = {};
    groups.forEach((group) => {
      options[group] = [
        ...new Set(missionMonitorings.map((elem) => elem.groups[group])),
      ];
    });
    return options;
  }, [groups, missionMonitorings]);

  const addAllGroupValues = (group) => {
    setGroupingObject({
      ...groupingObject,
      [group]: groupOptions[group],
    });
  };

  const removeAllGroupValues = (group) => {
    setGroupingObject((curr) => {
      const copy = { ...curr };
      delete copy[group];
      return copy;
    });
  };

  const addGroupValue = (group, value) => {
    setGroupingObject({
      ...groupingObject,
      [group]: [...new Set([...(groupingObject[group] ?? []), value])],
    });
  };

  const removeGroupValue = (group, value) => {
    const newGroupValue = [
      ...new Set(groupingObject[group].filter((val) => val !== value)),
    ];
    setGroupingObject((curr) => {
      if (newGroupValue.length > 0) {
        return {
          ...groupingObject,
          [group]: newGroupValue,
        };
      } else {
        const copy = { ...curr };
        delete copy[group];
        return copy;
      }
    });
  };

  const clearFilters = () => {
    setSelectedStatus(undefined);
    setSoftSelectedStatus(undefined);
    setSoftSelectedSubStatus(null);
    setFilterUnreadComments(false);
    setFilterActiveFlags(false);
    setFilterClientAction(false);
    setFilterHasResults(false);
    setFilterSiteName("");
    setGroupingObject({});
    setSoftGroupingObject(null);
  };

  const clearGroups = () => {
    setGroupingObject({});
    setSoftGroupingObject(null);
  };

  // Above filters applied to MMs
  const filteredMissionMonitorings = useMemo(() => {
    // If filtered on group
    return missionMonitorings.filter((elem) => {
      // only show missions the client can actually open results for.
      if (filterHasResults && !missionHasResults(elem, user.trials))
        return false;
      if (filterActiveFlags && elem.count_active_flags === 0)
        // Flags and comments
        return false;
      if (filterUnreadComments && elem.count_unread_comments === 0)
        return false;
      // Client action required — a free-text substatus, matched on its
      // normalised form (humanize only swaps "_" for spaces).
      if (filterClientAction && !missionRequiresClientAction(elem))
        return false;
      // Status
      if (selectedStatus !== undefined && elem.status !== selectedStatus)
        return false;
      return true;
    });
  }, [
    filterActiveFlags,
    filterUnreadComments,
    filterClientAction,
    filterHasResults,
    missionMonitorings,
    selectedStatus,
    user.trials,
  ]);

  const sitesCount = useMemo(
    () =>
      [...new Set(filteredMissionMonitorings.map(({ site }) => site.id))]
        .length,
    [filteredMissionMonitorings]
  );

  // Mission monitorings filtered using soft filters
  const softFilteredMissionMonitorings = useMemo(() => {
    const usedGroupingObject =
      softGroupingObject != null ? softGroupingObject : groupingObject;
    return filteredMissionMonitorings.filter((elem) => {
      // Filter on status or substatus if selected
      if (
        (softSelectedStatus && softSelectedStatus !== elem.status) ||
        (softSelectedSubStatus && softSelectedSubStatus !== elem.substatus)
      )
        return false;
      // Filter sites by name
      if (
        !elem.site.display_name
          .toLowerCase()
          .includes(filterSiteName.toLowerCase())
      )
        return false;
      // Filter by group key and value
      return shouldBeFilteredByGroups(elem, usedGroupingObject);
    });
  }, [
    filteredMissionMonitorings,
    softSelectedStatus,
    softSelectedSubStatus,
    filterSiteName,
    softGroupingObject,
    groupingObject,
  ]);

  // substatus in filtered missionMonitorings, filled when a status is selected
  const subStatus = useMemo(() => {
    return selectedStatus !== undefined
      ? Array.from(
          new Set(
            filteredMissionMonitorings.map((element) => element.substatus)
          )
        )
      : [];
  }, [filteredMissionMonitorings, selectedStatus]);

  // variable used by pie charts to determine level to count: status or substatus
  const dataCardKeyToCount =
    selectedStatus === undefined
      ? MiSSION_MONITORING_STATUS
      : MiSSION_MONITORING_SUBSTATUS;
  // Status list based on status or substatus if a status is selected
  const currentStatusList =
    selectedStatus === undefined ? statusList : subStatus;

  // Function that guarantees status color
  // Returns the base color when status, substatus color when a status is selected
  // Stable identity (useCallback) so the memoized overview/detail derivations
  // that depend on it don't recompute on unrelated re-renders.
  const getStatusColorNow = useCallback(
    (status) => getStatusColor(statusList, status, subStatus, selectedStatus),
    [statusList, subStatus, selectedStatus]
  );

  // On status click in legend
  const handleStatusClick = (status) => {
    if (selectedStatus === status) {
      setSelectedStatus(undefined);
      setSoftSelectedStatus(undefined);
    } else {
      setSelectedStatus(status);
      setSoftSelectedStatus(status);
    }
    setSoftSelectedSubStatus(null);
  };

  // On status click in a pie chart
  const handlePieClick = (label, soft) => {
    if (statusList.includes(label)) {
      if (!soft) handleStatusClick(label);
      else setSoftSelectedStatus(label);

      setSoftSelectedSubStatus(null);
    } else {
      // Keep the status filter (and its removable chip) while drilling into a
      // substatus, so there is always a way back out of the substatus level.
      setSoftSelectedSubStatus(label);
    }
  };

  // On slice/legend click in a per-group donut: drill the list into that group.
  const handleGroupClick = (label, combination) => {
    setSoftGroupingObject(Object.keys(combination).length ? combination : null);
    handlePieClick(label, true);
  };

  const [exportIsLoading, setExportIsLoading] = useState(false);
  const exportFilteredMissionMonitoringCsv = () => {
    setExportIsLoading(true);
    const fileName = `Monitoring_${slugify(
      selectedCompany.label,
      "_"
    )}_${moment().toJSON()}.csv`;
    const replacer = (key, value) => {
      return value == null || value === "∅" ? "" : value;
    };

    const promises = softFilteredMissionMonitorings.map((element) =>
      requestFetchMissionMonitoring(element, user)
    );

    Promise.all(promises).then((values) => {
      // All step names existing in values
      const uniqueSteps = [
        ...new Set(
          values.flatMap(({ steps }) => steps.map(({ name }) => name))
        ),
      ];

      const items = values.map((element) => {
        const item = {
          "Trial name": element.site.display_name,
          "Acquisition date": formatDate(element.acquisition_date),
          Status: element.status.replaceAll("_", " "),
          Substatus: element.substatus.replaceAll("_", " "),
          "Sensor type": element.sensor_type,
          Crop: element.crop,
          ...element.custom_data, // spread custom data
          Flags: element.flags
            .filter(({ closing_date }) => closing_date === null)
            .map(({ name, created_at }) => `${name}: ${formatDate(created_at)}`)
            .join(" | ")
            .trim(),
        };
        // Find every occurence of step name and spread date values
        uniqueSteps.forEach(
          (stepName) =>
            (item[stepName] = formatDate(
              element.steps.find(({ name }) => name === stepName)?.date
            ))
        );

        return item;
      });

      const header = Object.keys(items[0]);
      const csv = [
        header.join(";"), // header row first
        ...items.map((row) =>
          header
            .map((fieldName) => JSON.stringify(row[fieldName], replacer))
            .join(";")
        ),
      ].join("\n");
      download(csv, fileName);
      setExportIsLoading(false);
    });
  };

  const generateCombinationString = (combination) => {
    return Object.values(combination).length > 0
      ? Object.entries(combination)
          .map(([group, value]) => {
            return group === MISSION_MONITORING_STATIC_GROUPS.CONTRACT
              ? contracts.find((contract) => contract.id === value)?.name
              : value;
          })
          .join("·")
      : LABEL_ALL;
  };

  // Aggregate counts + status segments shared by the views' filters and charts.
  const flagCount = sumKey(
    filteredMissionMonitorings,
    MiSSION_MONITORING_FLAG_COUNT
  );
  const commentCount = sumKey(
    filteredMissionMonitorings,
    MiSSION_MONITORING_COMMENT_COUNT
  );

  // Overview donut: status (or substatus) distribution of the filtered set.
  const overviewSegments = useMemo(() => {
    const overviewOccurrences = occurrences(
      currentStatusList,
      filteredMissionMonitorings,
      dataCardKeyToCount
    );
    return currentStatusList
      .map((status) => ({
        label: humanize(status),
        value: overviewOccurrences[status] ?? 0,
        color: getStatusColorNow(status),
        filterValue: status,
      }))
      .filter((segment) => segment.value > 0);
  }, [
    currentStatusList,
    filteredMissionMonitorings,
    dataCardKeyToCount,
    getStatusColorNow,
  ]);

  // Per-facet, per-value count of dates (mission monitorings) — this is what the
  // filter actually narrows and what the donuts count, so it stays meaningful
  // for per-date dimensions (Plots, Gsd…) where a site spans many values.
  // Computed over the scope-filtered set, independent of the group selection.
  // ponytail: counts ignore other active facets; add cross-facet narrowing only
  // if users ask for live-narrowing facet counts.
  const groupValueCounts = useMemo(
    () =>
      groups.reduce((acc, group) => {
        const countByValue = {};
        const sensor = group === MISSION_MONITORING_STATIC_GROUPS.SENSOR;
        filteredMissionMonitorings.forEach((elem) => {
          const value = elem.groups?.[group];
          countByValue[value] = (countByValue[value] ?? 0) + 1;
        });
        // The sensor facet matches by containment, so its counts must too: "RGB"
        // has to announce the RGB + MS flights it will return, or the number on
        // the facet contradicts the list the click produces.
        if (sensor)
          Object.keys(countByValue).forEach((value) => {
            countByValue[value] = filteredMissionMonitorings.filter((elem) =>
              matchesSensorType([value], elem.groups?.[group])
            ).length;
          });
        acc[group] = countByValue;
        return acc;
      }, {}),
    [groups, filteredMissionMonitorings]
  );

  const filterSectionProps = {
    groups,
    groupOptions,
    groupingObject,
    groupValueCounts,
    addGroupValue,
    removeGroupValue,
    clearGroups,
    contracts,
    addAllGroupValues,
    removeAllGroupValues,
  };

  const mainListProps = {
    contracts,
    elements: softFilteredMissionMonitorings,
    statusList,
    getStatusColor: getStatusColorNow,
    refresh,
    user,
  };

  // Substatus chip colour, derived from its parent status (softSelectedStatus,
  // which persists) rather than the transient drill state, so it keeps its
  // colour after "All statuses" clears selectedStatus/subStatus.
  // Built from filteredMissionMonitorings (not the raw list) so the substatus
  // set matches the one the donut colours with — otherwise an active filter
  // shifts the chroma-scale index and the chip drifts off the slice colour.
  const softSelectedSubStatusColor = useMemo(() => {
    if (!softSelectedSubStatus) return undefined;
    const parentSubs = Array.from(
      new Set(
        filteredMissionMonitorings
          .filter((m) => !softSelectedStatus || m.status === softSelectedStatus)
          .map((m) => m.substatus)
      )
    );
    return getStatusColor(
      statusList,
      softSelectedSubStatus,
      parentSubs,
      softSelectedStatus
    );
  }, [
    softSelectedSubStatus,
    softSelectedStatus,
    filteredMissionMonitorings,
    statusList,
  ]);

  // Removable chips for the soft filters set by clicking a status/donut/group.
  const activeFilters = {
    softGroupingObject,
    setSoftGroupingObject,
    softSelectedStatus,
    setSoftSelectedStatus,
    softSelectedSubStatus,
    setSoftSelectedSubStatus,
    softSelectedSubStatusColor,
    setSelectedStatus,
    getStatusColor: getStatusColorNow,
    generateCombinationString,
  };

  // Any group value selected → show per-group donuts instead of the Overview.
  const hasGroups = Object.values(groupingObject).some((v) => v?.length);
  const detailGridProps = {
    data: filteredMissionMonitorings,
    statusList: currentStatusList,
    getStatusColor: getStatusColorNow,
    keyToCount: dataCardKeyToCount,
    handleGroupClick,
    groupingObject,
    generateCombinationString,
  };

  const campaignOptions = useMemo(
    () =>
      contracts
        .map((contract) => ({ value: contract.id, label: contract.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [contracts]
  );

  // Company + campaigns pickers, rendered inside the view's left rail panel.
  const scopeControls = (
    <>
      {companies.length > 1 && (
        <ReactSelect
          className="cf-rs"
          classNamePrefix="cf-rs"
          options={companyOptions}
          value={selectedCompany}
          onChange={setSelectedCompany}
          isDisabled={loading}
          aria-label="Company"
        />
      )}
      <label className="cf-field monitoring-scope__campaigns">
        <ReactSelect
          className="cf-rs"
          classNamePrefix="cf-rs"
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          backspaceRemovesValue={false}
          components={{
            Option: CheckOption,
            ValueContainer: CountValueContainer,
          }}
          options={campaignOptions}
          value={selectedContracts}
          onChange={(picked) => setSelectedContracts(picked)}
          placeholder="All campaigns"
          isDisabled={loading}
        />
      </label>
    </>
  );

  // Drilled into a status → a top-right "← All statuses" button pops back to the
  // status level without touching the soft filter (the chip and the filtered list
  // stay), so there's a way back from the card itself. Title stays "Overview".
  const overviewBack =
    selectedStatus === undefined ? undefined : (
      <button
        type="button"
        className="cf-dist__back"
        onClick={() => setSelectedStatus(undefined)}
      >
        <i className="fa-solid fa-arrow-left" /> All statuses
      </button>
    );

  const monitoringViewProps = {
    scopeControls,
    overviewBack,
    overviewSegments,
    onOverviewSelect: (label) => handlePieClick(label),
    filterSiteName,
    setFilterSiteName,
    flagCount,
    filterActiveFlags,
    setFilterActiveFlags,
    commentCount,
    filterUnreadComments,
    setFilterUnreadComments,
    filterClientAction,
    setFilterClientAction,
    filterHasResults,
    setFilterHasResults,
    activeFilters,
    filterSectionProps,
    mainListProps,
    hasGroups,
    detailGridProps,
  };

  return (
    <>
      <LoadingImg visible={loading} />
      <Container
        fluid
        className="page-content cf-page powerdash mission-monitoring-dashboard"
      >
        {companies.length === 0 ? (
          <ComponentPlaceholder text="It seems that you have no campaign available for visualization on this page" />
        ) : (
          <>
            <LevelHeader
              title="Monitoring"
              metrics={[
                { value: sitesCount, label: "sites" },
                { value: filteredMissionMonitorings.length, label: "dates" },
              ]}
              actions={
                <button
                  type="button"
                  className="cf-iconbtn"
                  onClick={exportFilteredMissionMonitoringCsv}
                  disabled={
                    exportIsLoading ||
                    softFilteredMissionMonitorings.length === 0
                  }
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Download CSV"
                  aria-label="Download CSV"
                >
                  <i
                    className={`fa-solid ${
                      exportIsLoading ? "fa-gear fa-spin" : "fa-download"
                    }`}
                  />
                </button>
              }
            />
            <MonitoringView {...monitoringViewProps} />
          </>
        )}
      </Container>
    </>
  );
};
